/**
 * R 域（重大资产）**端侧纯逻辑** —— 展示文本 / 分组 / 草稿与请求构造 · 批 92
 *
 * 依据：`R域PRD` v1.3 `AC-R1.1`–`R1.4` · `AC-R2.1`–`R2.5` · `AC-R4.1`–`R4.4` ·
 * `AC-R6.1`–`R6.6` · `AC-R7.1`–`R7.4` · `AC-R8.1`–`R8.5` · `DR-053` D1–D22 ·
 * 铁律 8（红=正）· 31（金额必带币种）· 63（可选键剔键）· 95（不变量分母）· 99（日期真实性）
 *
 * ## 🔴 本文件"一个业务数都不算"
 *
 * | 数 | 谁算 | 本文件做什么 |
 * |---|---|---|
 * | 累计投入 / 累计收益 / 账面盈亏 / 回报率 | **服务端**（`modules/asset` + `money.ts`） | 只把下发的整数搬成文本 |
 * | 关联贷款剩余 / 净权益 | **服务端**（Q 域余额口径） | 只渲染 |
 * | 账面净值 | **服务端**（内核 `majorAssetBookValue`） | 只渲染 `bookValueCents` |
 *
 * 唯一"看起来像计算"的三处，都不是业务算术：
 * - {@link percentWithSign}：**内核 `rateE4ToPercentText` 切字符串** + 加符号（不做除法）
 * - {@link areaToSquareCm}：单位换算（㎡ → 平方厘米，× 10000，`DR-053` D4）
 * - {@link buildXxx}：组装请求体（可选键按需剔键，不发 `undefined`）
 *
 * ## 🔴 三处"不在端侧再判"的纪律（批 89 同款）
 *
 * 1. **不判资金动作的业务约束**（首付 > 总价有服务端判；贷款载体缺失有服务端判）
 *    —— 端侧只拦"表单明显不一致"（如首付 > 总价这类一眼可见的输入错误）。
 * 2. **不判日期真实性**：`DayString` 的 `fromYmd(parseDay(d)) === d` 在**服务端**
 *    （`assertRealDay`，`AC-R2.4`）。端侧只验形态，真实日期交给 400。
 * 3. **不猜收益分类**：`MAJOR_ASSET_INCOME_CATEGORY_IDS` 由契约给出，端侧只做两选一。
 */

import {
  DepMethod,
  MajorAssetType,
  RepaymentMethod,
  centsToYuanText,
  formatMoneyText,
  percentTextToRateE4,
  type AdjustValuationRequest,
  type AppendInvestmentRequest,
  type Cents,
  type CreateMajorAssetRequest,
  type DisposeMajorAssetRequest,
  type MajorAssetType as MajorAssetTypeT,
  type MajorAssetView,
  type PurchaseMajorAssetRequest,
  type RecordIncomeRequest,
  type UpdateMajorAssetRequest,
} from '@jiaying/shared';
import {
  DEP_LABEL,
  DISPOSED_STATE_TEXT,
  HOLDING_STATE_TEXT,
  LOAN_COUNT_SUFFIX,
  LOAN_NONE_TEXT,
  NO_VALUATION_TEXT,
  PENDING_VALUATION_TEXT,
  TYPE_LABEL,
  TYPE_ORDER,
} from './major-asset-labels.js';
import { yuanTextToCentsOrNull } from './txn-draft.js';

// ─────────────────────────── ① 展示文本 ───────────────────────────

/**
 * 万分之一整数 → 带符号百分比文本。
 *
 * 🔴 实现已搬到 `features/percent-text.ts`（批 99 B2：投资域四处也要同一份格式）。
 * 这里**先 import 再再导出** —— 若只写 `export { … } from`，本文件作用域里没有这个绑定，
 * 下面的 `roiTextOf()` 会直接 `ReferenceError`（re-export 不等于本地声明）。
 * 既有调用点（`MajorAssetReturnPanel` / spec）与导入路径一字不改，全项目仍只有一份实现（铁律 24）。
 */
import { percentWithSign } from './percent-text.js';

export { percentWithSign };

/** `2023-03-18` → `2023-03`（列表行只到月，原型同）；坏值原样返回（不猜） */
export function monthTextOf(day: string | null): string {
  if (day === null || day.length < 7) return '';
  return day.slice(0, 7);
}

/**
 * 列表行副标题：`房产 · 沈琰 · 购入 2023-03 · 贷款买入`
 *
 * 四段都**只在有值时才出现**（购入日未填就不写"购入"）。
 */
export function assetSubtitle(v: MajorAssetView): string {
  const parts: string[] = [TYPE_LABEL[v.type]];
  if (v.ownerName !== '') parts.push(v.ownerName);
  const month = monthTextOf(v.purchaseDate);
  if (month !== '') parts.push(`购入 ${month}`);
  if (v.loanCount > 0) parts.push('贷款买入');
  else if (v.depMethod === DepMethod.Straight) parts.push(DEP_LABEL[DepMethod.Straight]);
  if (v.disposedDate !== null) parts.push(DISPOSED_STATE_TEXT);
  return parts.join(' · ');
}

/** 估值文本：`null` ⇒ 「未填估值」（🔴 不补 0 · `AC-R2.1`） */
export function valueTextOf(v: MajorAssetView): string {
  if (v.estimatedValue === null) return NO_VALUATION_TEXT;
  return formatMoneyText(v.estimatedValue as Cents, v.currency);
}

/** 回报率文本（🔴 只消费 `returnView.roiE4`，端侧零自算 · `AC-R7.2`） */
export function roiTextOf(v: MajorAssetView): string {
  const r = v.returnView;
  if (r.state === 'disposed') return DISPOSED_STATE_TEXT;
  if (r.state === 'unvalued' || r.roiE4 === null) return PENDING_VALUATION_TEXT;
  return percentWithSign(r.roiE4);
}

/** 回报率的着色方向：`+1` 涨 / `-1` 跌 / `0` 中性（🔴 红=正 · 铁律 8） */
export function roiDirectionOf(v: MajorAssetView): -1 | 0 | 1 {
  const roi = v.returnView.roiE4;
  if (roi === null || roi === 0) return 0;
  return roi > 0 ? 1 : -1;
}

/** 关联贷款数文本：`0` ⇒ 「无」（不是「0 笔」—— 那读起来像"有但为零"） */
export function loanCountTextOf(count: number): string {
  return count === 0 ? LOAN_NONE_TEXT : `${count}${LOAN_COUNT_SUFFIX}`;
}

/** 回报区状态文本（原型 `tag`：持有中 / 已处置） */
export function returnStateTextOf(v: MajorAssetView): string {
  return v.returnView.state === 'disposed' ? DISPOSED_STATE_TEXT : HOLDING_STATE_TEXT;
}

// ─────────────────────────── ② 分组（`AC-R4.2`） ───────────────────────────

export interface AssetGroup {
  type: MajorAssetTypeT;
  label: string;
  items: MajorAssetView[];
}

/**
 * 按 `type` 分组 —— **固定三组的顺序**，空组不出。
 *
 * 🔴 不做「按估值排序」：列表顺序 = 服务端下发序（新建在前）。按金额排会让用户
 * 每改一次估值就看到行位置跳动，而"大小"已经不是列表要回答的问题。
 */
export function groupAssets(items: readonly MajorAssetView[]): AssetGroup[] {
  return TYPE_ORDER.map((type) => ({
    type,
    label: TYPE_LABEL[type],
    items: items.filter((i) => i.type === type && i.disposedDate === null && !i.hidden),
  })).filter((g) => g.items.length > 0);
}

/** 隐藏的（折叠区）—— 仍计入 `items`，只是不进主分组 */
export function hiddenAssetsOf(items: readonly MajorAssetView[]): MajorAssetView[] {
  return items.filter((i) => i.hidden && i.disposedDate === null);
}

/** 主分组里的全部（= 计入净资产的那些；`AC-R4.3` 的列表侧） */
export function activeAssetsOf(items: readonly MajorAssetView[]): MajorAssetView[] {
  return items.filter((i) => !i.hidden && i.disposedDate === null);
}

// ─────────────────────────── ③ 估值曲线三态（`AC-R2.5`） ───────────────────────────

/**
 * 曲线状态 —— 三态**互斥**，绘制层据此决定画什么：
 *
 * | 状态 | 绘制 |
 * |---|---|
 * | `empty` | 只给空态文案，**不画任何图元** |
 * | `single` | 画 1 个点，**不画折线** |
 * | `multi` | 画点 + 折线（只连相邻真实点，不补中间值） |
 */
export function curveStateOf(pointCount: number): 'empty' | 'single' | 'multi' {
  if (pointCount <= 0) return 'empty';
  return pointCount === 1 ? 'single' : 'multi';
}

/** 曲线点数文本（原型右上角 `tag`：`4 点`） */
export function curveCountTextOf(pointCount: number): string {
  return `${pointCount} 点`;
}

// ─────────────────────────── ④ 台账表单草稿 ───────────────────────────

export interface AssetFormDraft {
  type: MajorAssetTypeT;
  name: string;
  ownerId: string;
  location: string;
  /** 面积以 **㎡** 录入（DB 存平方厘米 · `DR-053` D4） */
  areaText: string;
  purchaseCostText: string;
  purchaseDate: string;
  currency: string;
  estimatedValueText: string;
  valuationDate: string;
  depMethod: DepMethod;
  depYearsText: string;
  note: string;
  hidden: boolean;
}

/** ㎡ 文本 → 平方厘米；空串 = `null`；非法 = `undefined`（与 `account-draft` 同约定） */
export function areaToSquareCm(text: string): number | null | undefined {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return undefined;
  const n = Number(trimmed);
  // 单位换算（**不是金额算术**）：1 ㎡ = 10,000 平方厘米（`DR-053` D4 的存储单位）
  // eslint-disable-next-line no-restricted-syntax -- 面积单位换算，与金额定点无关
  return Math.round(n * 10000);
}

export function squareCmToAreaText(area: number | null): string {
  if (area === null) return '';
  // 反算只用于**回填输入框**（890000 → "89"，895000 → "89.5"）—— 同样是单位换算
  // eslint-disable-next-line no-restricted-syntax -- 面积单位换算，与金额定点无关
  const tenth = Math.round(area / 1000) / 10;
  // eslint-disable-next-line no-restricted-syntax -- 面积单位换算，与金额定点无关
  return Number.isInteger(tenth) ? String(tenth) : tenth.toFixed(1);
}

export function emptyAssetDraft(currency: string, ownerId: string): AssetFormDraft {
  return {
    type: MajorAssetType.Property,
    name: '',
    ownerId,
    location: '',
    areaText: '',
    purchaseCostText: '',
    purchaseDate: '',
    currency,
    estimatedValueText: '',
    valuationDate: '',
    depMethod: DepMethod.None,
    depYearsText: '',
    note: '',
    hidden: false,
  };
}

/**
 * 详情视图 → 全量草稿（`AC-R1.4` **整组替换**：必须预填全量）。
 *
 * 🔴 少预填一个字段，用户在编辑框里"只改名字"保存时那一个字段就会被**清空** ——
 * 这不是猜测，是 `PATCH` 整组替换语义的必然结果。
 */
export function assetDraftFromView(v: MajorAssetView): AssetFormDraft {
  return {
    type: v.type,
    name: v.name,
    ownerId: v.ownerId,
    location: v.location ?? '',
    areaText: squareCmToAreaText(v.area),
    purchaseCostText: v.purchaseCost === null ? '' : bareYuanText(v.purchaseCost as Cents),
    purchaseDate: v.purchaseDate ?? '',
    currency: v.currency,
    estimatedValueText: v.estimatedValue === null ? '' : bareYuanText(v.estimatedValue as Cents),
    valuationDate: v.valuationDate ?? '',
    depMethod: v.depMethod,
    depYearsText: v.depYears === null ? '' : String(v.depYears),
    note: v.note ?? '',
    hidden: v.hidden,
  };
}

/** 整数分 → 无符号无千分位的元文本（回填输入框；`123456` → `"1234.56"`） */
function bareYuanText(cents: Cents): string {
  // 🔴 走内核 `centsToYuanText`（定点、恒两位小数、**不带币种符与千分位**）—— 不自己拼字符串
  return centsToYuanText(cents);
}

const DAY_SHAPE = /^\d{4}-\d{2}-\d{2}$/;
const POSITIVE_INT = /^\d+$/;

export type DraftErrors = Record<string, string>;

/** 台账表单的**形态**校验（真日期、币种、跨域约束一律服务端判） */
export function validateAssetDraft(d: AssetFormDraft): DraftErrors {
  const e: DraftErrors = {};
  if (d.name.trim() === '') e.name = '请填写名称';
  if (d.ownerId.trim() === '') e.ownerId = '请选择归属人';
  if (d.purchaseDate.trim() !== '' && !DAY_SHAPE.test(d.purchaseDate.trim())) {
    e.purchaseDate = '日期须为 YYYY-MM-DD';
  }
  if (d.valuationDate.trim() !== '' && !DAY_SHAPE.test(d.valuationDate.trim())) {
    e.valuationDate = '日期须为 YYYY-MM-DD';
  }
  if (d.purchaseCostText.trim() !== '' && yuanTextToCentsOrNull(d.purchaseCostText) === null) {
    e.purchaseCost = '金额须为不超过两位小数的数字';
  }
  if (d.estimatedValueText.trim() !== '' && yuanTextToCentsOrNull(d.estimatedValueText) === null) {
    e.estimatedValue = '金额须为不超过两位小数的数字';
  }
  if (areaToSquareCm(d.areaText) === undefined) e.area = '面积须为不超过两位小数的数字';
  if (d.depMethod === DepMethod.Straight) {
    const t = d.depYearsText.trim();
    if (!POSITIVE_INT.test(t) || Number(t) <= 0) e.depYears = '直线法须填正整数年限（`AC-R2.2`）';
  }
  return e;
}

/** 空串 → `null`；非法 → 抛（调用前必过 {@link validateAssetDraft}） */
function centsOrNull(text: string): number | null {
  const t = text.trim();
  if (t === '') return null;
  const c = yuanTextToCentsOrNull(t);
  if (c === null) throw new Error(`金额非法：${text}`);
  return c;
}

function dayOrNull(text: string): string | null {
  const t = text.trim();
  return t === '' ? null : t;
}

/**
 * 草稿 → 新建请求（`POST /api/major-assets`）。
 *
 * 🔴 可选键**按需剔键**（铁律 63）：值为 `null` 的照发（表意"清空/未填"），
 * 只有"根本没这个字段"的（如 create 没有 `hidden`）才不发。
 */
export function buildCreateAssetRequest(d: AssetFormDraft): CreateMajorAssetRequest {
  const area = areaToSquareCm(d.areaText);
  return {
    type: d.type,
    name: d.name.trim(),
    ownerId: d.ownerId,
    location: d.location.trim() === '' ? null : d.location.trim(),
    area: area === undefined || area === null ? null : area,
    purchaseCost: centsOrNull(d.purchaseCostText),
    purchaseDate: dayOrNull(d.purchaseDate),
    currency: d.currency,
    estimatedValue: centsOrNull(d.estimatedValueText),
    valuationDate: dayOrNull(d.valuationDate),
    depMethod: d.depMethod,
    depYears: d.depMethod === DepMethod.Straight ? Number(d.depYearsText.trim()) : null,
    note: d.note.trim() === '' ? null : d.note.trim(),
  };
}

/** 草稿 → 编辑请求（`PATCH`，**整组替换** + `hidden` 开关） */
export function buildUpdateAssetRequest(d: AssetFormDraft): UpdateMajorAssetRequest {
  return { ...buildCreateAssetRequest(d), hidden: d.hidden };
}

// ─────────────────────────── ⑤ 五个动作草稿 ───────────────────────────

/** 调整估值（`AC-R7.1`） */
export interface ValuationDraft {
  valueText: string;
  date: string;
  note: string;
}

export function emptyValuationDraft(today: string, current: number | null): ValuationDraft {
  return {
    valueText: current === null ? '' : bareYuanText(current as Cents),
    date: today,
    note: '',
  };
}

export function validateValuationDraft(d: ValuationDraft): DraftErrors {
  const e: DraftErrors = {};
  if (yuanTextToCentsOrNull(d.valueText) === null) e.value = '请填写估值金额（不超过两位小数）';
  if (!DAY_SHAPE.test(d.date.trim())) e.date = '日期须为 YYYY-MM-DD';
  return e;
}

export function buildValuationRequest(d: ValuationDraft): AdjustValuationRequest {
  const cents = centsOrNull(d.valueText);
  if (cents === null) throw new Error('估值未填');
  return {
    estimatedValue: cents,
    valuationDate: d.date.trim(),
    note: d.note.trim() === '' ? null : d.note.trim(),
  };
}

/** 购入（`AC-R6.1` / `R6.2`） */
export interface PurchaseDraft {
  date: string;
  totalText: string;
  downText: string;
  fromAccountId: string;
  /** 已有贷款账户（`''` = 不选） */
  loanAccountId: string;
  /** 新建贷款账户（`name === ''` = 不新建） */
  newLoanName: string;
  newLoanCounterparty: string;
  annualRateText: string;
  termsText: string;
  startDate: string;
  dueDate: string;
  method: '' | RepaymentMethod;
  note: string;
}

export function emptyPurchaseDraft(today: string): PurchaseDraft {
  return {
    date: today,
    totalText: '',
    downText: '',
    fromAccountId: '',
    loanAccountId: '',
    newLoanName: '',
    newLoanCounterparty: '',
    annualRateText: '',
    termsText: '',
    startDate: '',
    dueDate: '',
    method: '',
    note: '',
  };
}

export function validatePurchaseDraft(d: PurchaseDraft): DraftErrors {
  const e: DraftErrors = {};
  const total = yuanTextToCentsOrNull(d.totalText);
  if (total === null || total === 0) e.total = '请填写购入总价（大于 0）';
  if (!DAY_SHAPE.test(d.date.trim())) e.date = '日期须为 YYYY-MM-DD';
  if (d.fromAccountId === '') e.fromAccountId = '请选择首付付款账户';

  const downText = d.downText.trim();
  const down = downText === '' ? 0 : yuanTextToCentsOrNull(downText);
  if (down === null) e.down = '首付须为不超过两位小数的数字';
  if (total !== null && down !== null && down > total) e.down = '首付不得大于总价（服务端同样校验）';

  if (d.loanAccountId !== '' && d.newLoanName.trim() !== '') {
    e.newLoanName = '已有贷款账户与新建贷款只能选一个';
  }
  if (d.annualRateText.trim() !== '' && yuanTextToCentsOrNull(d.annualRateText) === null) {
    e.annualRate = '年利率须为数字';
  }
  if (d.termsText.trim() !== '' && !POSITIVE_INT.test(d.termsText.trim())) {
    e.terms = '期数须为正整数';
  }
  return e;
}

/**
 * 购入请求 —— 贷款金额**不由端侧传**（= 总价 − 首付，服务端算）。
 *
 * 🔴 `newLoan` 只在**填了名字**时出现（`name` 是它的存在标志）—— 否则一律不发，
 * 避免"传了个空壳对象"让服务端以为要建账户。
 */
export function buildPurchaseRequest(d: PurchaseDraft): PurchaseMajorAssetRequest {
  const total = centsOrNull(d.totalText);
  if (total === null) throw new Error('总价未填');
  const down = d.downText.trim() === '' ? 0 : centsOrNull(d.downText);
  const req: PurchaseMajorAssetRequest = {
    date: d.date.trim(),
    totalCents: total,
    fromAccountId: d.fromAccountId,
    note: d.note.trim() === '' ? null : d.note.trim(),
  };
  if (down !== null && down > 0) req.downPaymentCents = down;
  if (d.loanAccountId !== '') req.loanAccountId = d.loanAccountId;

  if (d.newLoanName.trim() !== '') {
    const terms = d.termsText.trim();
    const rate = d.annualRateText.trim();
    req.newLoan = {
      name: d.newLoanName.trim(),
      counterparty: d.newLoanCounterparty.trim() === '' ? null : d.newLoanCounterparty.trim(),
      // 🔴 期限块六项全可空（`DR-044`）：没填就不发，不发 `undefined`
      annualRateE4: rate === '' ? null : rateE4OfPercentText(rate),
      terms: terms === '' ? null : Number(terms),
      startDate: d.startDate.trim() === '' ? null : (d.startDate.trim()),
      dueDate: d.dueDate.trim() === '' ? null : (d.dueDate.trim()),
      method: d.method === '' ? null : d.method,
    };
  }
  return req;
}

/** 百分数文本 → 万分之一整数（`3.85` → `38500`）；非法返回 `null` 由校验层拦 */
function rateE4OfPercentText(text: string): number | null {
  // 🔴 换算**只经内核** `percentTextToRateE4()`（禁浮点、禁第二份实现 · 铁律 24）
  try {
    return percentTextToRateE4(text);
  } catch {
    return null;
  }
}

/** 追加投资（`AC-R6.3`） */
export interface AppendDraft {
  amountText: string;
  date: string;
  fromAccountId: string;
  note: string;
}

export function emptyAppendDraft(today: string): AppendDraft {
  return { amountText: '', date: today, fromAccountId: '', note: '' };
}

export function validateAppendDraft(d: AppendDraft): DraftErrors {
  const e: DraftErrors = {};
  const amount = yuanTextToCentsOrNull(d.amountText);
  if (amount === null || amount === 0) e.amount = '请填写金额（大于 0）';
  if (!DAY_SHAPE.test(d.date.trim())) e.date = '日期须为 YYYY-MM-DD';
  if (d.fromAccountId === '') e.fromAccountId = '请选择付款账户';
  return e;
}

export function buildAppendRequest(d: AppendDraft): AppendInvestmentRequest {
  const amount = centsOrNull(d.amountText);
  if (amount === null) throw new Error('金额未填');
  return {
    date: d.date.trim(),
    amountCents: amount,
    fromAccountId: d.fromAccountId,
    note: d.note.trim() === '' ? null : d.note.trim(),
  };
}

/** 记收益（`AC-R6.4`） */
export interface IncomeDraft {
  amountText: string;
  date: string;
  toAccountId: string;
  categoryId: string;
  note: string;
}

export function emptyIncomeDraft(today: string, defaultCategoryId: string): IncomeDraft {
  return { amountText: '', date: today, toAccountId: '', categoryId: defaultCategoryId, note: '' };
}

export function validateIncomeDraft(d: IncomeDraft, allowed: readonly string[]): DraftErrors {
  const e: DraftErrors = {};
  const amount = yuanTextToCentsOrNull(d.amountText);
  if (amount === null || amount === 0) e.amount = '请填写金额（大于 0）';
  if (!DAY_SHAPE.test(d.date.trim())) e.date = '日期须为 YYYY-MM-DD';
  if (d.toAccountId === '') e.toAccountId = '请选择收款账户';
  // 🔴 分类合法性来自**契约白名单**（`MAJOR_ASSET_INCOME_CATEGORY_IDS`），不在这里另写一份
  if (!allowed.includes(d.categoryId)) e.categoryId = '分类只能二选一（租金收入 / 投资收益）';
  return e;
}

export function buildIncomeRequest(d: IncomeDraft): RecordIncomeRequest {
  const amount = centsOrNull(d.amountText);
  if (amount === null) throw new Error('金额未填');
  return {
    date: d.date.trim(),
    amountCents: amount,
    toAccountId: d.toAccountId,
    categoryId: d.categoryId as RecordIncomeRequest['categoryId'],
    note: d.note.trim() === '' ? null : d.note.trim(),
  };
}

/** 处置（`AC-R8.1`–`R8.5`） */
export interface DisposeDraft {
  proceedsText: string;
  date: string;
  toAccountId: string;
  settleLoans: boolean;
  note: string;
}

export function emptyDisposeDraft(today: string, hasLoans: boolean): DisposeDraft {
  return { proceedsText: '', date: today, toAccountId: '', settleLoans: hasLoans, note: '' };
}

export function validateDisposeDraft(d: DisposeDraft): DraftErrors {
  const e: DraftErrors = {};
  const proceeds = yuanTextToCentsOrNull(d.proceedsText);
  // 🔴 `0` **合法**（报废）⇒ 判据是"解析得出来"，不是"大于 0"（`AC-R8.1`）
  if (proceeds === null) e.proceeds = '请填写实得价（0 = 报废）';
  if (!DAY_SHAPE.test(d.date.trim())) e.date = '日期须为 YYYY-MM-DD';
  if (proceeds !== null && proceeds > 0 && d.toAccountId === '') {
    e.toAccountId = '实得价大于 0 时须选收款账户';
  }
  return e;
}

export function buildDisposeRequest(d: DisposeDraft): DisposeMajorAssetRequest {
  const proceeds = centsOrNull(d.proceedsText);
  if (proceeds === null) throw new Error('实得价未填');
  return {
    date: d.date.trim(),
    proceedsCents: proceeds,
    toAccountId: proceeds > 0 && d.toAccountId !== '' ? d.toAccountId : null,
    settleLoans: d.settleLoans,
    note: d.note.trim() === '' ? null : d.note.trim(),
  };
}

/** 是否有校验错误（表单据此禁用提交按钮） */
export function hasErrors(e: DraftErrors): boolean {
  return Object.keys(e).length > 0;
}
